import { c as _c } from "react/compiler-runtime";
import { IcoChevronDown16 } from '@onefootprint/icons';
import type { ChatSession } from '@onefootprint/request-types/dashboard';
import { Dialog, Dropdown, TextInput } from '@onefootprint/ui';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ASK_PERCY_ROUTE, chatSessionUrl } from '@/config/constants';
import { useChatSessionMutations, useChatSessionsList } from '@/features/ask-percy';
import ChatHistoryMenu from '../chat-history-menu';
export type ChatHistoryDropdownProps = {
  /** The id of the session whose title to render in the trigger and check in the list. */
  activeSessionId: string | null;
  /** Called when a chat is picked. Defaults to `router.push(/chat/<id>)`. */
  onSelectSession?: (id: string) => void;
  /** Called when "New chat" is picked. Defaults to `router.push(/ask-percy)`. */
  onNewChat?: () => void;
  /** Optional class for the title trigger button. */
  className?: string;
  /** Open the menu on mount. Useful for snapshots/stories. */
  defaultOpen?: boolean;
};
const triggerClass = 'flex items-center justify-between gap-1 h-7 px-2 w-full rounded-(--button-border-radius) hover:bg-hover data-[state=open]:bg-senary/70 cursor-pointer min-w-0 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-primary';
const ChatHistoryDropdown = t0 => {
  const $ = _c(129);
  const {
    activeSessionId,
    onSelectSession,
    onNewChat,
    className,
    defaultOpen
  } = t0;
  const router = useRouter();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "chatHistoryDropdown"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const {
    sessions,
    isLoading
  } = useChatSessionsList();
  const {
    toggleStar,
    renameSession,
    deleteSession
  } = useChatSessionMutations();
  const [open, setOpen] = useState(!!defaultOpen);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  let T0;
  let t10;
  let t11;
  let t12;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[1] !== activeSessionId || $[2] !== className || $[3] !== deleteSession || $[4] !== deleteTarget || $[5] !== isLoading || $[6] !== onNewChat || $[7] !== onSelectSession || $[8] !== open || $[9] !== renameSession || $[10] !== renameTarget || $[11] !== renameValue || $[12] !== router || $[13] !== sessions || $[14] !== t || $[15] !== toggleStar) {
    const active = activeSessionId ? sessions.find(s => s.id === activeSessionId) : undefined;
    const fallback = activeSessionId ? t(_temp) : t(_temp2);
    const resolvedTitle = active?.title ?? fallback;
    let t13;
    if ($[28] !== activeSessionId || $[29] !== onSelectSession || $[30] !== router) {
      t13 = id => {
        if (id === activeSessionId) {
          return;
        }
        if (onSelectSession) {
          onSelectSession(id);
          return;
        }
        router.push(chatSessionUrl(id));
      };
      $[28] = activeSessionId;
      $[29] = onSelectSession;
      $[30] = router;
      $[31] = t13;
    } else {
      t13 = $[31];
    }
    const handleSelect = t13;
    let t14;
    if ($[32] !== onNewChat || $[33] !== router) {
      t14 = () => {
        if (onNewChat) {
          onNewChat();
          return;
        }
        router.push(ASK_PERCY_ROUTE);
      };
      $[32] = onNewChat;
      $[33] = router;
      $[34] = t14;
    } else {
      t14 = $[34];
    }
    const handleNewChat = t14;
    let t15;
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = session => {
        setOpen(false);
        setRenameValue(session.title ?? "");
        setRenameTarget(session);
      };
      $[35] = t15;
    } else {
      t15 = $[35];
    }
    const handleOpenRename = t15;
    let t16;
    if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = session_0 => {
        setOpen(false);
        setDeleteTarget(session_0);
      };
      $[36] = t16;
    } else {
      t16 = $[36];
    }
    const handleOpenRemove = t16;
    let t17;
    if ($[37] !== renameSession || $[38] !== renameTarget || $[39] !== renameValue) {
      t17 = () => {
        if (!renameTarget) {
          return;
        }
        const trimmed = renameValue.trim();
        if (trimmed && trimmed !== renameTarget.title) {
          renameSession(renameTarget.id, trimmed);
        }
        setRenameTarget(null);
      };
      $[37] = renameSession;
      $[38] = renameTarget;
      $[39] = renameValue;
      $[40] = t17;
    } else {
      t17 = $[40];
    }
    const handleConfirmRename = t17;
    let t18;
    if ($[41] !== activeSessionId || $[42] !== deleteSession || $[43] !== deleteTarget || $[44] !== onNewChat || $[45] !== router) {
      t18 = () => {
        if (!deleteTarget) {
          return;
        }
        if (deleteTarget.id === activeSessionId) {
          if (onNewChat) {
            onNewChat();
          } else {
            router.push(ASK_PERCY_ROUTE);
          }
        }
        deleteSession(deleteTarget.id);
        setDeleteTarget(null);
      };
      $[41] = activeSessionId;
      $[42] = deleteSession;
      $[43] = deleteTarget;
      $[44] = onNewChat;
      $[45] = router;
      $[46] = t18;
    } else {
      t18 = $[46];
    }
    const handleConfirmDelete = t18;
    const deleteName = deleteTarget?.title || t(_temp3);
    const t19 = t(_temp4, {
      title: resolvedTitle
    });
    const t20 = className ?? triggerClass;
    let t21;
    if ($[47] !== resolvedTitle) {
      t21 = <span className="text-label-3 text-primary truncate flex-1 min-w-0 text-left max-md:py-1 max-md:text-[0.875rem]/[1.25rem]! max-md:[text-box:trim-both_cap_alphabetic]">{resolvedTitle}</span>;
      $[47] = resolvedTitle;
      $[48] = t21;
    } else {
      t21 = $[48];
    }
    let t22;
    if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <span className="shrink-0 flex items-center"><IcoChevronDown16 /></span>;
      $[49] = t22;
    } else {
      t22 = $[49];
    }
    let t23;
    if ($[50] !== t19 || $[51] !== t20 || $[52] !== t21) {
      t23 = <Dropdown.Trigger asChild={true} width="fit-content" maxWidth="100%"><button type="button" aria-label={t19} className={t20}>{t21}{t22}</button></Dropdown.Trigger>;
      $[50] = t19;
      $[51] = t20;
      $[52] = t21;
      $[53] = t23;
    } else {
      t23 = $[53];
    }
    let t24;
    if ($[54] !== activeSessionId || $[55] !== handleNewChat || $[56] !== handleSelect || $[57] !== isLoading || $[58] !== sessions || $[59] !== toggleStar) {
      t24 = <Dropdown.Portal><Dropdown.Content align="start" sideOffset={4} className="w-[304px] max-h-[400px] overflow-y-auto p-1"><ChatHistoryMenu sessions={sessions} isLoading={isLoading} activeSessionId={activeSessionId} onSelect={handleSelect} onNewChat={handleNewChat} onStar={toggleStar} onRename={handleOpenRename} onRemove={handleOpenRemove} /></Dropdown.Content></Dropdown.Portal>;
      $[54] = activeSessionId;
      $[55] = handleNewChat;
      $[56] = handleSelect;
      $[57] = isLoading;
      $[58] = sessions;
      $[59] = toggleStar;
      $[60] = t24;
    } else {
      t24 = $[60];
    }
    if ($[61] !== open || $[62] !== t23 || $[63] !== t24) {
      t11 = <Dropdown.Root open={open} onOpenChange={setOpen} modal={false}>{t23}{t24}</Dropdown.Root>;
      $[61] = open;
      $[62] = t23;
      $[63] = t24;
      $[64] = t11;
    } else {
      t11 = $[64];
    }
    const t25 = !!renameTarget;
    let t26;
    if ($[65] !== t) {
      t26 = t(_temp5);
      $[65] = t;
      $[66] = t26;
    } else {
      t26 = $[66];
    }
    let t27;
    if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
      t27 = () => setRenameTarget(null);
      $[67] = t27;
    } else {
      t27 = $[67];
    }
    let t28;
    if ($[68] !== t) {
      t28 = t(_temp6);
      $[68] = t;
      $[69] = t28;
    } else {
      t28 = $[69];
    }
    let t29;
    if ($[70] !== renameValue) {
      t29 = renameValue.trim();
      $[70] = renameValue;
      $[71] = t29;
    } else {
      t29 = $[71];
    }
    const t30 = !t29;
    let t31;
    if ($[72] !== handleConfirmRename || $[73] !== t28 || $[74] !== t30) {
      t31 = {
        label: t28,
        onClick: handleConfirmRename,
        disabled: t30
      };
      $[72] = handleConfirmRename;
      $[73] = t28;
      $[74] = t30;
      $[75] = t31;
    } else {
      t31 = $[75];
    }
    let t32;
    if ($[76] !== t) {
      t32 = t(_temp7);
      $[76] = t;
      $[77] = t32;
    } else {
      t32 = $[77];
    }
    let t33;
    if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
      t33 = () => setRenameTarget(null);
      $[78] = t33;
    } else {
      t33 = $[78];
    }
    let t34;
    if ($[79] !== t32) {
      t34 = {
        label: t32,
        onClick: t33
      };
      $[79] = t32;
      $[80] = t34;
    } else {
      t34 = $[80];
    }
    let t35;
    if ($[81] !== t) {
      t35 = t(_temp8);
      $[81] = t;
      $[82] = t35;
    } else {
      t35 = $[82];
    }
    let t36;
    if ($[83] !== t) {
      t36 = t(_temp9);
      $[83] = t;
      $[84] = t36;
    } else {
      t36 = $[84];
    }
    let t37;
    if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
      t37 = e => setRenameValue(e.target.value);
      $[85] = t37;
    } else {
      t37 = $[85];
    }
    let t38;
    if ($[86] !== handleConfirmRename) {
      t38 = e_0 => {
        if (e_0.key === "Enter") {
          e_0.preventDefault();
          handleConfirmRename();
        }
      };
      $[86] = handleConfirmRename;
      $[87] = t38;
    } else {
      t38 = $[87];
    }
    let t39;
    if ($[88] !== renameValue || $[89] !== t35 || $[90] !== t36 || $[91] !== t38) {
      t39 = <TextInput label={t35} placeholder={t36} size="compact" value={renameValue} onChange={t37} onKeyDown={t38} autoFocus={true} />;
      $[88] = renameValue;
      $[89] = t35;
      $[90] = t36;
      $[91] = t38;
      $[92] = t39;
    } else {
      t39 = $[92];
    }
    if ($[93] !== t25 || $[94] !== t26 || $[95] !== t31 || $[96] !== t34 || $[97] !== t39) {
      t12 = <Dialog open={t25} title={t26} onClose={t27} size="compact" primaryButton={t31} secondaryButton={t34}>{t39}</Dialog>;
      $[93] = t25;
      $[94] = t26;
      $[95] = t31;
      $[96] = t34;
      $[97] = t39;
      $[98] = t12;
    } else {
      t12 = $[98];
    }
    T0 = Dialog;
    t4 = !!deleteTarget;
    if ($[99] !== t) {
      t5 = t(_temp10);
      $[99] = t;
      $[100] = t5;
    } else {
      t5 = $[100];
    }
    if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = () => setDeleteTarget(null);
      $[101] = t6;
    } else {
      t6 = $[101];
    }
    t7 = "compact";
    t8 = true;
    let t40;
    if ($[102] !== t) {
      t40 = t(_temp11);
      $[102] = t;
      $[103] = t40;
    } else {
      t40 = $[103];
    }
    if ($[104] !== handleConfirmDelete || $[105] !== t40) {
      t9 = {
        label: t40,
        variant: "destructive",
        onClick: handleConfirmDelete
      };
      $[104] = handleConfirmDelete;
      $[105] = t40;
      $[106] = t9;
    } else {
      t9 = $[106];
    }
    let t41;
    if ($[107] !== t) {
      t41 = t(_temp12);
      $[107] = t;
      $[108] = t41;
    } else {
      t41 = $[108];
    }
    let t42;
    if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
      t42 = () => setDeleteTarget(null);
      $[109] = t42;
    } else {
      t42 = $[109];
    }
    if ($[110] !== t41) {
      t10 = {
        label: t41,
        onClick: t42
      };
      $[110] = t41;
      $[111] = t10;
    } else {
      t10 = $[111];
    }
    t2 = "text-body-3 text-secondary";
    t3 = t(_temp13, {
      name: deleteName
    });
    $[1] = activeSessionId;
    $[2] = className;
    $[3] = deleteSession;
    $[4] = deleteTarget;
    $[5] = isLoading;
    $[6] = onNewChat;
    $[7] = onSelectSession;
    $[8] = open;
    $[9] = renameSession;
    $[10] = renameTarget;
    $[11] = renameValue;
    $[12] = router;
    $[13] = sessions;
    $[14] = t;
    $[15] = toggleStar;
    $[16] = T0;
    $[17] = t10;
    $[18] = t11;
    $[19] = t12;
    $[20] = t2;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    T0 = $[16];
    t10 = $[17];
    t11 = $[18];
    t12 = $[19];
    t2 = $[20];
    t3 = $[21];
    t4 = $[22];
    t5 = $[23];
    t6 = $[24];
    t7 = $[25];
    t8 = $[26];
    t9 = $[27];
  }
  let t13;
  if ($[112] !== t2 || $[113] !== t3) {
    t13 = <p className={t2}>{t3}</p>;
    $[112] = t2;
    $[113] = t3;
    $[114] = t13;
  } else {
    t13 = $[114];
  }
  let t14;
  if ($[115] !== T0 || $[116] !== t10 || $[117] !== t13 || $[118] !== t4 || $[119] !== t5 || $[120] !== t6 || $[121] !== t7 || $[122] !== t8 || $[123] !== t9) {
    t14 = <T0 open={t4} title={t5} onClose={t6} size={t7} isConfirmation={t8} primaryButton={t9} secondaryButton={t10}>{t13}</T0>;
    $[115] = T0;
    $[116] = t10;
    $[117] = t13;
    $[118] = t4;
    $[119] = t5;
    $[120] = t6;
    $[121] = t7;
    $[122] = t8;
    $[123] = t9;
    $[124] = t14;
  } else {
    t14 = $[124];
  }
  let t15;
  if ($[125] !== t11 || $[126] !== t12 || $[127] !== t14) {
    t15 = <>{t11}{t12}{t14}</>;
    $[125] = t11;
    $[126] = t12;
    $[127] = t14;
    $[128] = t15;
  } else {
    t15 = $[128];
  }
  return t15;
};
export default ChatHistoryDropdown;
function _temp($) {
  return $.untitled;
}
function _temp2($_0) {
  return $_0.newChat;
}
function _temp3($_1) {
  return $_1.untitled;
}
function _temp4($_2) {
  return $_2.triggerAriaLabel;
}
function _temp5($_3) {
  return $_3.rename.title;
}
function _temp6($_4) {
  return $_4.rename.confirm;
}
function _temp7($_5) {
  return $_5.rename.cancel;
}
function _temp8($_6) {
  return $_6.rename.nameLabel;
}
function _temp9($_7) {
  return $_7.rename.namePlaceholder;
}
function _temp10($_8) {
  return $_8.delete.title;
}
function _temp11($_9) {
  return $_9.delete.confirm;
}
function _temp12($_10) {
  return $_10.delete.cancel;
}
function _temp13($_11) {
  return $_11.delete.body;
}